import {
  Hoofdstuk,
  Sectie,
  SubscriptionStatus,
  Vraag,
  VraagOrComingSoon
} from "./types";
import { TheorieGroep } from "@/services/api_types";
import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

/**
 * Returns a short display code for a vraag within a sectie.
 * Voorkennis exercises get labeled V1, V2, ... based on position.
 */
export const getVraagDisplayCode = (vraag: Vraag, sectie: Sectie): string => {
  if (/^voorkennis/i.test(sectie.titel)) {
    const idx = sectie.vragen.findIndex((v) => v.id === vraag.id);
    return `V${idx + 1}`;
  }
  return vraag.code;
};

/**
 * Abbreviates a vraag code for display when no sectie context is available.
 * "Voorkennis" → "VK". All other codes pass through unchanged.
 */
export const abbreviateVraagCode = (code: string): string => {
  if (/^voorkennis/i.test(code)) return "VK";
  return code;
};

// Helper function to capitalize the first letter of each word
export const capitalizeFirstLetter = (str: string) => {
  return str.replace(/\b\w/g, (char) => char.toUpperCase());
};

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const isDateTodayOrFuture = (dateValue?: string | null) => {
  if (!dateValue) return false;

  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return false;

  const today = new Date();
  const dateDay = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate()
  ).getTime();
  const todayDay = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate()
  ).getTime();

  return dateDay >= todayDay;
};

export const formatTitel = (titel: string) => {
  let result;

  const match = titel.match(/^H(\d+)/);
  if (match) {
    result = `Hoofdstuk ${match[1]}`;
  } else if (titel.includes("HK")) {
    result = titel.replace("HK", "Hoofdstuk Keuze");
  } else {
    result = titel;
  }

  return capitalizeFirstLetter(result);
};

export const sortSecties = (hoofdstuk: Hoofdstuk): Sectie[] => {
  // Section priority - lower index = higher priority (appears first)
  // Patterns match the START of the title (case-insensitive)
  const priorityPatterns: RegExp[] = [
    /^voorkennis/i, // Voorkennis...
    /^opgaven/i, // Opgaven...
    /^para/i, // Paragraaf, Para...
    /^(diagnostische|d-?toets|dia\s*toets)/i, // Diagnostische Toets, D-Toets, Dia toets...
    /^gemengde/i, // Gemengde Opgaven...
    /^wiswiz/i // WisWiz Oefentoets...
  ];

  const getPriority = (titel: string): number => {
    for (let i = 0; i < priorityPatterns.length; i++) {
      if (priorityPatterns[i].test(titel)) return i;
    }
    return priorityPatterns.length; // Unrecognized sections appear after known ones
  };

  return hoofdstuk.secties.slice().sort((a, b) => {
    const aPriority = getPriority(a.titel);
    const bPriority = getPriority(b.titel);

    // Different priority → sort by priority
    if (aPriority !== bPriority) {
      return aPriority - bPriority;
    }

    // Same priority → sort by extracted number, then alphabetically
    const extractNumber = (titel: string): number => {
      const match = titel.match(/(\d+(?:\.\d+)?)/);
      return match ? parseFloat(match[1]) : Infinity;
    };

    const aNum = extractNumber(a.titel);
    const bNum = extractNumber(b.titel);

    if (aNum !== bNum) {
      if (aNum === Infinity) return 1;
      if (bNum === Infinity) return -1;
      return aNum - bNum;
    }

    return a.titel.localeCompare(b.titel);
  });
};

/**
 * Sort TheorieGroep[] by the section position of their vragen,
 * using the same priority logic as sortSecties.
 */
export function sortTheorieGroepen(
  groepen: TheorieGroep[],
  hoofdstuk: Hoofdstuk
): TheorieGroep[] {
  const sortedSecties = sortSecties(hoofdstuk);
  const vraagIdToSectiePos = new Map<number, number>();
  for (let i = 0; i < sortedSecties.length; i++) {
    for (const vraag of sortedSecties[i].vragen) {
      vraagIdToSectiePos.set(vraag.id, i);
    }
  }

  const getMinSectiePos = (groep: TheorieGroep): number =>
    groep.vragen.reduce(
      (min, v) =>
        Math.min(min, vraagIdToSectiePos.get(v.id) ?? Number.MAX_SAFE_INTEGER),
      Number.MAX_SAFE_INTEGER
    );

  return groepen.slice().sort((a, b) => {
    const posA = getMinSectiePos(a);
    const posB = getMinSectiePos(b);
    if (posA !== posB) return posA - posB;
    return a.volgorde - b.volgorde;
  });
}

/** Strip "Theorie X " prefix from theorie group titles. */
export function formatTheorieTitel(titel: string): string {
  return (titel ?? "").replace(/^Theorie\s+[A-Z]\s+/i, "");
}

/** Exercise-only groups have an empty titel (in-between questions with no theory). */
export function isExerciseOnlyGroep(groep: TheorieGroep): boolean {
  return groep.titel === "";
}

/**
 * Derive a label for an exercise-only group from its question codes.
 * e.g. "Opgave 18" (single) or "Opgaven 18, 22" (multiple).
 */
export function getExerciseOnlyLabel(groep: TheorieGroep): string {
  const codes = groep.vragen.map((v) => v.code);
  if (codes.length === 0) return "Opgaven";
  if (codes.length === 1) return `Opgave ${codes[0]}`;
  return `Opgaven ${codes.join(", ")}`;
}

/**
 * Group a sectie's vragen by their linked theorie.
 * If only 0–1 groups exist, returns flat (titel: null) to avoid a lone header.
 */
export function groupVragenByTheorie(
  vragen: Vraag[],
  theorieGroepen: TheorieGroep[]
): { titel: string | null; vragen: Vraag[] }[] {
  const relevantGroups: { titel: string; vragen: Vraag[] }[] = [];
  const assignedIds = new Set<number>();

  for (const groep of theorieGroepen) {
    const groepIdSet = new Set(groep.vragen.map((gv) => gv.id));
    const matching = vragen.filter((v) => groepIdSet.has(v.id));
    if (matching.length > 0) {
      relevantGroups.push({
        titel: formatTheorieTitel(groep.titel),
        vragen: matching
      });
      matching.forEach((v) => assignedIds.add(v.id));
    }
  }

  const unassigned = vragen.filter((v) => !assignedIds.has(v.id));
  if (unassigned.length > 0) {
    relevantGroups.push({ titel: "Overig", vragen: unassigned });
  }

  // 0 or 1 groups → stay flat
  if (relevantGroups.length <= 1) {
    return [{ titel: null, vragen }];
  }

  // Sort groups by lowest volgorde_nummer of their vragen
  relevantGroups.sort((a, b) => {
    const aMin = Math.min(...a.vragen.map((v) => v.volgorde_nummer));
    const bMin = Math.min(...b.vragen.map((v) => v.volgorde_nummer));
    return aMin - bMin;
  });

  return relevantGroups;
}

export const sortHoofdstukken = (hoofdstukken: Hoofdstuk[]): Hoofdstuk[] => {
  return hoofdstukken.slice().sort((a, b) => chapterSort(a.titel, b.titel));
};

// Huidige datum genereren (in dd-mm-jjjj formaat)
export function formatDateToLabel(date: Date) {
  const day = date.getDate().toString().padStart(2, "0");
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const year = date.getFullYear().toString();
  const label = `${day}-${month}-${year}`;
  return label;
}

export function formatDateToLongLabel(date: Date) {
  return date.toLocaleDateString("nl-NL", {
    year: "numeric",
    month: "long",
    day: "numeric"
  });
}

// Helper function to extract the chapter number from a title
const extractNumberFromTitle = (title: string) => {
  const match = title.match(/\d+/); // Match digits in the title
  return match ? parseInt(match[0], 10) : null;
};

export const chapterSort = (a: string, b: string) => {
  // "Gemengde opgaven" always last
  if (a === "Gemengde opgaven") return 1;
  if (b === "Gemengde opgaven") return -1;

  // H{number} chapters come before other items
  const aIsH = /^H\d+$/i.test(a);
  const bIsH = /^H\d+$/i.test(b);

  if (aIsH && !bIsH) return -1;
  if (!aIsH && bIsH) return 1;

  const aNum = extractNumberFromTitle(a);
  const bNum = extractNumberFromTitle(b);

  const aHasNumber = aNum !== null;
  const bHasNumber = bNum !== null;

  if (aHasNumber && bHasNumber) {
    return aNum - bNum;
  }

  if (aHasNumber && !bHasNumber) {
    return -1;
  }

  if (!aHasNumber && bHasNumber) {
    return 1;
  }

  return a.localeCompare(b);
};

export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

// Demo widget pathname utilities
export const isDemoWidgetPage = (pathname: string): boolean => {
  return pathname === "/scholen" || pathname === "/";
};

export const shouldDisableScrollEffect = (pathname: string): boolean => {
  return isDemoWidgetPage(pathname);
};

const SUBSCRIPTION_STATUS_LABELS: Record<SubscriptionStatus, string> = {
  pending: "In verwerking",
  active: "Actief",
  paused: "Gepauzeerd",
  past_due: "Betalingsprobleem",
  canceled: "Geannuleerd"
};

export function formatSubscriptionStatus(
  status?: SubscriptionStatus
): string | undefined {
  if (status) {
    return SUBSCRIPTION_STATUS_LABELS[status];
  } else {
    return undefined;
  }
}

// TODO: TEMPORARY — remove fillVraagGaps, parseCode, PREFIX_NUM when all vragen gaps are filled
const PREFIX_NUM = /^([A-Za-z]*)(\d+)$/;

function parseCode(code: string) {
  const m = code.match(PREFIX_NUM);
  if (!m) return null;
  return { prefix: m[1], num: parseInt(m[2], 10) };
}

export function fillVraagGaps(vragen: Vraag[]): VraagOrComingSoon[] {
  if (vragen.length === 0) return [];

  // Collect all existing codes to avoid duplicate placeholders
  const existingCodes = new Set(vragen.map((v) => v.code));

  const result: VraagOrComingSoon[] = [vragen[0]];

  for (let i = 1; i < vragen.length; i++) {
    const prev = parseCode(vragen[i - 1].code);
    const curr = parseCode(vragen[i].code);

    // Only fill gaps when both codes share the same prefix (including both empty)
    if (prev && curr && prev.prefix === curr.prefix) {
      for (let n = prev.num + 1; n < curr.num; n++) {
        const code = prev.prefix + String(n);
        if (!existingCodes.has(code)) {
          result.push({ comingSoon: true, code });
        }
      }
    }

    result.push(vragen[i]);
  }

  return result;
}
